import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { KPI_CATEGORIES, REVIEW_PERIODS } from './KPIHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// New Review Dialog
export const NewReviewDialog = ({ open, onOpenChange, employees, templates, onSuccess }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    template_id: '',
    review_period: 'monthly',
    period_start: '',
    period_end: ''
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (!formData.employee_id || !formData.template_id) {
      toast.error('Please select employee and template');
      return;
    }
    
    if (!formData.period_start || !formData.period_end) {
      toast.error('Please select period start and end dates');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kpi/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Review created');
        setFormData({
          employee_id: '',
          template_id: '',
          review_period: 'monthly',
          period_start: '',
          period_end: ''
        });
        onSuccess();
      } else {
        throw new Error(data.detail || 'Failed to create review');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New KPI Review</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select 
              value={formData.employee_id} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, employee_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.employee_code} - {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Template</Label>
            <Select 
              value={formData.template_id} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, template_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Review Period</Label>
            <Select 
              value={formData.review_period} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, review_period: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REVIEW_PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Period Start</Label>
              <Input 
                type="date" 
                value={formData.period_start}
                onChange={(e) => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
              />
            </div>
            <div>
              <Label>Period End</Label>
              <Input 
                type="date" 
                value={formData.period_end}
                onChange={(e) => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Creating...' : 'Create Review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Review Dialog
export const EditReviewDialog = ({ open, onOpenChange, review, onSuccess }) => {
  const [scores, setScores] = useState(review?.scores || []);
  const [overallComment, setOverallComment] = useState(review?.overall_comment || '');
  const [status, setStatus] = useState(review?.status || 'draft');
  const [loading, setLoading] = useState(false);
  
  const updateScore = (index, field, value) => {
    setScores(prev => prev.map((s, i) => 
      i === index ? { ...s, [field]: value } : s
    ));
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kpi/reviews/${review.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores, overall_comment: overallComment, status })
      });
      
      if (res.ok) {
        toast.success('Review updated');
        onSuccess();
      } else {
        throw new Error('Failed to update review');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit KPI Review - {review?.employee_name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {KPI_CATEGORIES.map(cat => {
            const catScores = scores.filter(s => s.category === cat);
            if (catScores.length === 0) return null;
            
            return (
              <div key={cat} className="border rounded-lg p-4">
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Badge>{cat}</Badge>
                </h4>
                <div className="space-y-3">
                  {catScores.map((score, idx) => {
                    const globalIdx = scores.findIndex(s => s.metric_id === score.metric_id);
                    return (
                      <div key={score.metric_id} className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-4">
                          <p className="font-medium text-sm">{score.metric_name}</p>
                          <p className="text-xs text-gray-500">Weight: {score.weightage}%</p>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Target</Label>
                          <Input value={score.target_value} disabled className="h-8" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Actual</Label>
                          <Input 
                            type="number"
                            value={score.actual_value}
                            onChange={(e) => updateScore(globalIdx, 'actual_value', parseFloat(e.target.value) || 0)}
                            className="h-8"
                          />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-xs">Comment</Label>
                          <Input 
                            value={score.comment || ''}
                            onChange={(e) => updateScore(globalIdx, 'comment', e.target.value)}
                            placeholder="Optional comment"
                            className="h-8"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          
          <div>
            <Label>Overall Comment</Label>
            <Textarea 
              value={overallComment}
              onChange={(e) => setOverallComment(e.target.value)}
              rows={3}
              placeholder="Overall feedback and comments..."
            />
          </div>
          
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? 'Saving...' : 'Save Review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Template Editor Dialog
export const TemplateEditorDialog = ({ open, onOpenChange, template, departments, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    department: '',
    position: '',
    description: '',
    metrics: []
  });
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        department: template.department || '',
        position: template.position || '',
        description: template.description || '',
        metrics: template.metrics || []
      });
    } else {
      setFormData({
        name: '',
        department: '',
        position: '',
        description: '',
        metrics: []
      });
    }
  }, [template]);
  
  const addMetric = () => {
    setFormData(prev => ({
      ...prev,
      metrics: [...prev.metrics, {
        id: Date.now().toString(),
        name: '',
        description: '',
        category: 'Performance',
        target_value: 100,
        weightage: 10,
        measurement_type: 'percentage',
        is_higher_better: true
      }]
    }));
  };
  
  const updateMetric = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: prev.metrics.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };
  
  const removeMetric = (index) => {
    setFormData(prev => ({
      ...prev,
      metrics: prev.metrics.filter((_, i) => i !== index)
    }));
  };
  
  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Please enter template name');
      return;
    }
    
    setLoading(true);
    try {
      const url = template 
        ? `${API_URL}/api/kpi/templates/${template.id}`
        : `${API_URL}/api/kpi/templates`;
      
      const res = await fetch(url, {
        method: template ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(template ? 'Template updated' : 'Template created');
        onSuccess();
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const totalWeightage = formData.metrics.reduce((sum, m) => sum + (m.weightage || 0), 0);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Create New Template'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Template Name *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., FOH Staff KPIs"
              />
            </div>
            <div>
              <Label>Department (optional)</Label>
              <Select 
                value={formData.department || 'none'} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, department: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (All Departments)</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              placeholder="Brief description of this template..."
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-lg">Metrics</Label>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${totalWeightage === 100 ? 'text-green-600' : 'text-red-600'}`}>
                  Total Weight: {totalWeightage}%
                </span>
                <Button size="sm" onClick={addMetric}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Metric
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {formData.metrics.map((metric, index) => (
                <div key={metric.id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-3">
                      <Label className="text-xs">Metric Name</Label>
                      <Input 
                        value={metric.name}
                        onChange={(e) => updateMetric(index, 'name', e.target.value)}
                        placeholder="e.g., Sales Target"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Category</Label>
                      <Select 
                        value={metric.category} 
                        onValueChange={(v) => updateMetric(index, 'category', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KPI_CATEGORIES.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Target</Label>
                      <Input 
                        type="number"
                        value={metric.target_value}
                        onChange={(e) => updateMetric(index, 'target_value', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Weight %</Label>
                      <Input 
                        type="number"
                        value={metric.weightage}
                        onChange={(e) => updateMetric(index, 'weightage', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Description</Label>
                      <Input 
                        value={metric.description || ''}
                        onChange={(e) => updateMetric(index, 'description', e.target.value)}
                        placeholder="Brief desc..."
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeMetric(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
